import axios, { AxiosRequestConfig, Method } from "axios";
import { PublicConfig } from "../PublicConfig";

/** Payment type for the IPH/PetPartners Paymentech iframe request. */
export enum PaymentechPaymentType {
    ECP = 1,
    CreditCard = 2
}

export interface PaymentechIframeRequest {
    paymentType: PaymentechPaymentType;
    amount?: string; // e.g. "12.00"
    order_id?: string;
    billing_name?: string;
    txn_type?: string; // e.g. "ProfileAdd" (per IPH docs; configurable later)
}

export interface PaymentechIframeResponse {
    uid: string;
    status?: string;
    status_code?: string;
    status_message?: string;
}

/**
 * Client → same-origin proxy request. Intentionally carries NO amount/billing fields, because the
 * uid does not represent a priced transaction: `txn_type: "ProfileAdd"` mints a card-TOKENIZATION
 * session, so nothing about the charge is fixed when it is created and a tampered client request has
 * no amount to tamper with.
 *
 * The charge is assembled later, at finalize: spot-api re-reads the quote and posts the captured token
 * together with the billing frequency to Orca's checkout (`OrcaService.checkout` →
 * `paymentMethod` + `paymentFrequencyId`). Frequency isn't even on the Orca quote — spot-api keeps it
 * in its own scratchpad (see `PTZCAQuoteAPI`: "Persist billing frequency (not stored in Orca v2 API)").
 *
 * Practical consequence: a uid stays valid across quote edits. Do NOT re-mint it when the quote changes
 * (e.g. monthly → annual) — the uid carries no amount, and re-minting reloads the hosted iframe, which
 * wipes any card details the customer has already typed.
 */
export interface ChaseIframeClientRequest {
    quoteId: string;
    paymentType: PaymentechPaymentType;
}

/**
 * Browser-side helper: requests the Chase HPF `uid` via the same-origin Next.js
 * proxy route (the third-party endpoint can't be called directly due to CORS).
 */
export async function fetchChaseIframeUid(payload: ChaseIframeClientRequest): Promise<PaymentechIframeResponse> {
    const res = await axios.post<PaymentechIframeResponse>(`${PublicConfig.BASE_PATH}/api/ptz-ca/payment-session`, payload);
    return res.data;
}

/**
 * Server-side client for the IPH/PetPartners billing API used to bootstrap the
 * Chase Hosted Payment Form (HPF). Step 1 of the flow requests a `uid` that is
 * then used to mount the Chase-hosted iframe. Used by the API proxy route.
 */
export class PaymentechAPI {
    private readonly baseUrl: string;

    constructor(baseUrl: string = PublicConfig.PTZ_CA.PAYMENT_API_BASE_URL) {
        this.baseUrl = baseUrl;
    }

    private async call<T = any>(method: Method, endpoint: string, options: AxiosRequestConfig = {}): Promise<T> {
        const url = this.baseUrl + endpoint;
        const request = await axios(url, {
            ...options,
            method,
            headers: {
                "Content-Type": "application/json",
                ...options.headers
            }
        });
        return request.data;
    }

    /** Requests a `uid` for mounting the hosted payment iframe. */
    async createIframeUid(payload: PaymentechIframeRequest): Promise<PaymentechIframeResponse> {
        const data = await this.call<PaymentechIframeResponse | string>("POST", "/billing/v1/paymentech/iframe", { data: payload });
        // The endpoint may return the body as an unparsed JSON string (non-JSON Content-Type),
        // in which case axios skips parsing — normalize to an object so `.uid` is accessible.
        if (typeof data === "string") {
            try {
                return JSON.parse(data) as PaymentechIframeResponse;
            } catch {
                throw new Error("Unexpected non-JSON response from Paymentech iframe endpoint");
            }
        }
        return data;
    }
}
